import { Box, Collapse, Divider, Grid, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import type { FC } from "react";
import { useMemo } from "react";
import { DRAWER_WIDTH_IN_PX } from "../../../components/RecommendationsDrawer/utils";
import { type DayStats, NurseFullRole, type NurseInShift, type ShiftPerson } from "../../../utils/models";
import { formatNumber } from "../../../utils/utilFunctions";

const LONG_TERM_CARE_PERCENT = 0.75;
const SHORT_TERM_CARE_PERCENT = 0.25;

type NursingAndAidHours = { nursingHours: number; aidHours: number };

type SnfDemoCareStatsProps = {
	expanded: boolean;
	drawerOpen: boolean;
	nurses: NurseInShift[];
	activeStats: DayStats;
	getNurseById: (id: number) => ShiftPerson | undefined;
};

export const SnfDemoCareStats: FC<SnfDemoCareStatsProps> = ({ expanded, drawerOpen, nurses, activeStats, getNurseById }) => {
	const nursingAndAidHours = useMemo<NursingAndAidHours>(() => {
		let nursingHours = 0;
		let aidHours = 0;

		nurses.forEach((nurseShift) => {
			const nurseDetails = getNurseById(nurseShift.nurseId);
			if (!nurseDetails) return;

			const hours = nurseShift.endTime.diff(nurseShift.startTime, "hour").as("hours");

			if (nurseShift.role === NurseFullRole.RN || nurseShift.role === NurseFullRole.LPN) {
				nursingHours += hours;
			} else if (nurseShift.role === NurseFullRole.CNA) {
				aidHours += hours;
			}
		});

		return { nursingHours, aidHours };
	}, [nurses, getNurseById]);

	return (
		<Collapse in={expanded}>
			<Box
				sx={{
					background: "white",
					px: 2,
					py: 1,
					boxShadow: "0px 4px 8px 0px rgba(107, 104, 153, 0.15)",
					marginRight: drawerOpen ? `${DRAWER_WIDTH_IN_PX}px` : 0,
					transition: "margin-right 225ms cubic-bezier(0.4, 0, 0.6, 1) 0ms",
					borderTop: "1px solid #e0e0e0",
					display: "flex",
					gap: 3,
				}}
			>
				{/* Long Term Care Section */}
				<Box sx={{ flex: 1 }}>
					<Typography sx={{ fontSize: "16px", fontWeight: 600, color: "primary.dark", mb: 0.5 }}>Long Term Care</Typography>
					<Grid container sx={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 2 }}>
						<Box>
							<Typography sx={{ fontSize: "12px", fontWeight: 400, color: (theme) => alpha(theme.palette.primary.main, 0.7), mb: 0.5 }}>Census</Typography>
							<Typography sx={{ fontSize: "16px", fontWeight: 600, color: "primary.dark" }}>
								{Math.round(activeStats.census * LONG_TERM_CARE_PERCENT)}
							</Typography>
						</Box>
						<Box>
							<Typography sx={{ fontSize: "12px", fontWeight: 400, color: (theme) => alpha(theme.palette.primary.main, 0.7), mb: 0.5 }}>HPPD</Typography>
							<Typography sx={{ fontSize: "16px", fontWeight: 600, color: "primary.dark" }}>
								{formatNumber(activeStats.hppd * LONG_TERM_CARE_PERCENT)}
								<Typography component="span" sx={{ fontSize: "14px", fontWeight: 600, color: "primary.dark", opacity: 0.5, ml: 0.5 }}>
									{" "}
									/ 2.75{" "}
								</Typography>
							</Typography>
						</Box>
						<Box>
							<Typography sx={{ fontSize: "12px", fontWeight: 400, color: (theme) => alpha(theme.palette.primary.main, 0.7), mb: 0.5 }}>
								Nursing HPPD
							</Typography>
							<Typography sx={{ fontSize: "16px", fontWeight: 600, color: "primary.dark" }}>
								{formatNumber((nursingAndAidHours.nursingHours / activeStats.census) * LONG_TERM_CARE_PERCENT)}
							</Typography>
						</Box>
						<Box>
							<Typography sx={{ fontSize: "12px", fontWeight: 400, color: (theme) => alpha(theme.palette.primary.main, 0.7), mb: 0.5 }}>Aide HPPD</Typography>
							<Typography sx={{ fontSize: "16px", fontWeight: 600, color: "primary.dark" }}>
								{formatNumber((nursingAndAidHours.aidHours / activeStats.census) * LONG_TERM_CARE_PERCENT)}
							</Typography>
						</Box>
					</Grid>
				</Box>

				<Divider orientation="vertical" flexItem sx={{ borderColor: "#e0e0e0" }} />

				{/* Rehabilitation Care Section */}
				<Box sx={{ flex: 1 }}>
					<Typography sx={{ fontSize: "16px", fontWeight: 600, color: "primary.dark", mb: 0.5 }}>Rehabilitation</Typography>
					<Grid container sx={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 2 }}>
						<Box>
							<Typography sx={{ fontSize: "12px", fontWeight: 400, color: (theme) => alpha(theme.palette.primary.main, 0.7), mb: 0.5 }}>Census</Typography>
							<Typography sx={{ fontSize: "16px", fontWeight: 600, color: "primary.dark" }}>
								{Math.round(activeStats.census * SHORT_TERM_CARE_PERCENT)}
							</Typography>
						</Box>
						<Box>
							<Typography sx={{ fontSize: "12px", fontWeight: 400, color: (theme) => alpha(theme.palette.primary.main, 0.7), mb: 0.5 }}>HPPD</Typography>
							<Typography sx={{ fontSize: "16px", fontWeight: 600, color: "primary.dark" }}>
								{formatNumber(activeStats.hppd * SHORT_TERM_CARE_PERCENT)}
								<Typography component="span" sx={{ fontSize: "14px", fontWeight: 600, color: "primary.dark", opacity: 0.5, ml: 0.5 }}>
									{" "}
									/ 5{" "}
								</Typography>
							</Typography>
						</Box>
						<Box>
							<Typography sx={{ fontSize: "12px", fontWeight: 400, color: (theme) => alpha(theme.palette.primary.main, 0.7), mb: 0.5 }}>
								Nursing HPPD
							</Typography>
							<Typography sx={{ fontSize: "16px", fontWeight: 600, color: "primary.dark" }}>
								{formatNumber((nursingAndAidHours.nursingHours / activeStats.census) * SHORT_TERM_CARE_PERCENT)}
							</Typography>
						</Box>
						<Box>
							<Typography sx={{ fontSize: "12px", fontWeight: 400, color: (theme) => alpha(theme.palette.primary.main, 0.7), mb: 0.5 }}>Aide HPPD</Typography>
							<Typography sx={{ fontSize: "16px", fontWeight: 600, color: "primary.dark" }}>
								{formatNumber((nursingAndAidHours.aidHours / activeStats.census) * SHORT_TERM_CARE_PERCENT)}
							</Typography>
						</Box>
					</Grid>
				</Box>
			</Box>
		</Collapse>
	);
};
